(() => {
  const loader = document.currentScript;
  const projectId = loader?.dataset.projectId;
  const storefrontHost = loader?.dataset.storefrontHost;
  const report = error => console.error('[Seravni Clarity]', error);

  if (!projectId || !/^[a-z0-9]+$/.test(projectId) || !storefrontHost) {
    report('Missing or invalid tracking configuration. Clarity was not loaded.');
    return;
  }
  if (document.querySelectorAll('script[data-seravni-clarity]').length !== 1 || window.clarity) {
    report('An existing Clarity integration was detected. A second tracker was not loaded.');
    return;
  }

  const privatePath = /^\/(?:accounts?|checkouts?|password|orders?|apps|auth|oauth|login|register|challenge|gift_cards)(?:\/|$)/i;
  const publicPath = /^\/$|^\/(?:products|collections|pages|blogs|policies|search|cart)(?:\/|$)/i;
  const privateContent = '[data-seravni-clarity-exclude], form[action*="/account"], form[action*="/checkout"], input[type="password"], input[autocomplete*="cc-"]';
  const campaignParameters = new Set(['utm_source', 'utm_medium', 'utm_campaign', 'utm_content']);
  const clickParameters = new Set(['gclid', 'gbraid', 'wbraid', 'msclkid']);
  const numericParameters = new Set(['variant', 'gad_source', 'gad_campaignid', 'page']);
  const sortParameters = new Set(['manual', 'best-selling', 'title-ascending', 'title-descending', 'price-ascending', 'price-descending', 'created-ascending', 'created-descending', 'relevance']);

  function normalizedPath(url) {
    try {
      return decodeURIComponent(url.pathname).replace(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/)/i, '');
    } catch (error) {
      if (!(error instanceof URIError)) throw error;
      report('The page URL could not be decoded safely. Clarity was not loaded.');
      return null;
    }
  }

  function privateUrl(url) {
    const path = normalizedPath(url);
    if (path === null || privatePath.test(path) || /^accounts?\./i.test(url.hostname)) return true;
    if (url.hash && !/^#[a-z][a-z0-9_-]{0,120}$/i.test(url.hash)) return true;
    for (const [name, value] of url.searchParams) {
      if (campaignParameters.has(name) && /^[a-z0-9._~ -]{1,160}$/i.test(value)) continue;
      if (clickParameters.has(name) && /^[a-z0-9_-]{1,300}$/i.test(value)) continue;
      if (numericParameters.has(name) && /^\d{1,24}$/.test(value)) continue;
      if (name === 'gclsrc' && /^[a-z.]{1,16}$/.test(value)) continue;
      if (name === 'seravni_qa' && value === 'true') continue;
      if (name === 'sort_by' && sortParameters.has(value)) continue;
      if (name === 'filter.v.availability' && /^(0|1)$/.test(value)) continue;
      if (/^filter\.[pv]\.price\.(gte|lte)$/.test(name) && /^\d{1,7}(?:\.\d{1,2})?$/.test(value)) continue;
      return true;
    }
    return false;
  }

  function eligibleUrl() {
    const url = new URL(window.location.href);
    if (url.protocol !== 'https:' || url.hostname !== storefrontHost || !publicPath.test(normalizedPath(url)) || privateUrl(url)) {
      return false;
    }
    return !document.referrer || !privateUrl(new URL(document.referrer));
  }

  let privacyReady = false;
  let pageEligible = loader.dataset.pageEligible === 'true';
  let optedOut = false;
  let inserted = false;
  let active = false;
  let failed = false;
  let generation = 0;

  function grantConsent() {
    window.clarity('consentv2', {
      ad_Storage: 'denied',
      analytics_Storage: 'granted',
    });
  }

  function stop() {
    if (!active) return;
    // Consent denial clears identity and schedules cookieless recording. Stop capture instead.
    // Shopify permission still gates every start, including after actual withdrawal.
    window.clarity('stop');
    active = false;
  }

  function reconcile() {
    if (!privacyReady || failed) return;
    const allowed = !optedOut && pageEligible && eligibleUrl() && !document.querySelector(privateContent) &&
      window.Shopify.customerPrivacy.analyticsProcessingAllowed() === true;
    if (!allowed) {
      stop();
      return;
    }
    if (active) return;

    let qa;
    try {
      if (new URL(window.location.href).searchParams.get('seravni_qa') === 'true') {
        window.sessionStorage.setItem('seravni_clarity_qa', 'true');
      }
      qa = window.sessionStorage.getItem('seravni_clarity_qa') === 'true';
    } catch (error) {
      failed = true;
      report(error);
      return;
    }

    if (!inserted) {
      if (window.clarity) {
        failed = true;
        report('Another tracker appeared before consent. Clarity was not loaded twice.');
        return;
      }
      window.clarity = (...args) => window.clarity.q.push(args);
      window.clarity.q = [];
      grantConsent();
      window.clarity('set', 'seravni_tracking_version', 'content_scope_v3');
      window.clarity('set', 'seravni_qa', qa ? 'true' : 'false');
      const tag = document.createElement('script');
      tag.async = true;
      tag.src = `https://www.clarity.ms/tag/${projectId}`;
      tag.dataset.seravniClarityTag = '';
      tag.onload = reconcile;
      tag.onerror = () => {
        failed = true;
        stop();
        report('The Clarity tag could not be loaded.');
      };
      inserted = true;
      active = true;
      document.head.appendChild(tag);
    } else {
      grantConsent();
      window.clarity('start', { projectId });
      window.clarity('set', 'seravni_tracking_version', 'content_scope_v3');
      window.clarity('set', 'seravni_qa', qa ? 'true' : 'false');
      active = true;
    }
  }

  const preferences = document.querySelector('template[data-seravni-clarity-preferences]');
  const policyBody = document.querySelector('.shopify-policy__body');
  if (preferences && policyBody) policyBody.appendChild(preferences.content.cloneNode(true));
  const optOutButtons = document.querySelectorAll('[data-seravni-clarity-opt-out]');
  const optOutStatus = document.querySelector('[data-seravni-clarity-opt-out-status]');
  for (const button of optOutButtons) {
    button.addEventListener('click', () => {
      optedOut = true;
      stop();
      if (!privacyReady || typeof window.Shopify.customerPrivacy.setTrackingConsent !== 'function') {
        report('Analytics permission could not be saved.');
        if (optOutStatus) {
          optOutStatus.hidden = false;
          optOutStatus.textContent = 'Recording stopped on this page, but your browser could not save the choice.';
        }
        return;
      }
      button.disabled = true;
      window.Shopify.customerPrivacy.setTrackingConsent({ analytics: false }, error => {
        button.disabled = false;
        const saved = !error && window.Shopify.customerPrivacy.analyticsProcessingAllowed() === false;
        if (!saved) report(error || 'Analytics permission was not changed.');
        reconcile();
        if (optOutStatus) {
          optOutStatus.hidden = false;
          optOutStatus.textContent = saved
            ? 'Analytics recording is turned off on this browser.'
            : 'Recording stopped on this page, but your browser could not save the choice.';
        }
      });
    });
  }
  if ((!pageEligible || !eligibleUrl()) && !optOutButtons.length) return;

  document.addEventListener('visitorConsentCollected', event => {
    if (event.detail?.analyticsAllowed === true) optedOut = false;
    reconcile();
  });
  const contentObserver = new MutationObserver(reconcile);
  contentObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-seravni-clarity-exclude', 'type', 'autocomplete', 'action'],
  });
  window.addEventListener('popstate', reconcile);
  window.addEventListener('hashchange', reconcile);
  for (const method of ['pushState', 'replaceState']) {
    const original = window.history[method];
    window.history[method] = function (...args) {
      const result = original.apply(this, args);
      reconcile();
      return result;
    };
  }
  window.addEventListener('pagehide', () => {
    generation++;
    pageEligible = false;
    stop();
  });
  window.addEventListener('pageshow', event => {
    if (!event.persisted || !eligibleUrl()) return;
    const check = ++generation;
    pageEligible = false;
    stop();
    // Revalidate the current template after restoring a cached document.
    fetch(window.location.href, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'text/html' } })
      .then(async response => {
        if (!response.ok || response.redirected) throw new Error('Could not revalidate the restored storefront page.');
        const html = await response.text();
        const restored = new DOMParser().parseFromString(html, 'text/html');
        const marker = restored.querySelector('script[data-seravni-clarity]');
        if (check !== generation) return;
        pageEligible = marker?.dataset.pageEligible === 'true' &&
          marker?.dataset.projectId === projectId && marker?.dataset.storefrontHost === storefrontHost;
        reconcile();
      })
      .catch(error => report(error));
  });

  function ready(error) {
    if (error || typeof window.Shopify?.customerPrivacy?.analyticsProcessingAllowed !== 'function') {
      report(error || 'Shopify analytics permission could not be verified. Clarity was not loaded.');
      return;
    }
    privacyReady = true;
    reconcile();
  }

  if (typeof window.Shopify?.customerPrivacy?.analyticsProcessingAllowed === 'function') {
    ready();
  } else if (typeof window.Shopify?.loadFeatures === 'function') {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.2' }], ready);
  } else {
    report('Shopify privacy controls are unavailable. Clarity was not loaded.');
  }
})();
